import { useState, useEffect, useCallback } from 'react';

export type Language = 'en' | 'de';

const LANGUAGE_KEY = 'preferred_language';

export function useLanguage() {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LANGUAGE_KEY);
      if (stored === 'en' || stored === 'de') return stored;
      // Default to English
      return 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem(LANGUAGE_KEY, language);
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState(prev => prev === 'en' ? 'de' : 'en');
  }, []);

  return { language, setLanguage, toggleLanguage };
}

// Helper to get localized content with fallback
export function getLocalizedContent<T extends Record<string, unknown>>(
  item: T,
  field: string,
  language: Language
): string {
  if (language === 'en') {
    const enField = `${field}_en` as keyof T;
    const enValue = item[enField];
    if (enValue && typeof enValue === 'string' && enValue.trim()) {
      return enValue;
    }
  }
  // Fallback to original field
  const value = item[field as keyof T];
  return typeof value === 'string' ? value : '';
}
